import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["url", "modifier", "imageId", "enableLightbox", "name", "toggleModal", "handleSelectedImage", "displayCount", "lightboxImages", "isHero", "priority", "type", "areImageCreditsDisplayed", "isCaptionDisplayed", "images", "imageData", "imageUrl", "elementTitle", "credits", "imageCredits", "children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from '@blaze-react/utils/lib/customHooks';
import Wrapper from '../Wrapper';
import ImageRender from './ImageRender';
import ImageDetails from './ImageDetails';
import getClassModifiers from '../../utils/get-class-modifiers';
import { getImageData } from '../../utils';
import { getDisplayCountData, shouldShowProperty, buildImageProperties } from '../../helpers';
import { IMAGE, HERO, IN_VIEW_CONFIG } from '../../constants';
const Image = _ref => {
  let {
      url,
      modifier: _modifier,
      imageId,
      enableLightbox,
      name,
      toggleModal,
      handleSelectedImage,
      displayCount,
      lightboxImages,
      isHero,
      priority,
      type,
      areImageCreditsDisplayed,
      isCaptionDisplayed,
      images,
      imageData,
      imageUrl,
      elementTitle,
      credits,
      imageCredits,
      children
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const [displayLightbox, setDisplayLightbox] = useState(false);
  const [isIntersecting, outerRef] = useInView(IN_VIEW_CONFIG);
  const {
    caption,
    altText,
    hrefUrl,
    credits: imageDataCredits
  } = getImageData(otherProps, imageData);
  const href = url || hrefUrl;
  const {
    ImgWrapper,
    imgWrapperProps,
    lightboxImgProps
  } = buildImageProperties({
    href,
    name,
    enableLightbox,
    isHero
  });
  const modifier = _modifier || '';
  const modifiers = getClassModifiers(type, _objectSpread({
    modifier
  }, otherProps));
  const wrapperClassName = isHero ? HERO : IMAGE;
  const toggleLightbox = () => setDisplayLightbox(!displayLightbox);
  const handleEnableLightbox = () => {
    if (!enableLightbox) return;
    toggleModal();
    handleSelectedImage(imageId);
  };
  const shouldDisplayCaption = shouldShowProperty({
    isDisplayed: isCaptionDisplayed,
    value: caption
  });

  // deprecated: imageCredits is deprecated. This code is to handle legacy data. check before changing/removing
  const creditsValue = credits || imageCredits || imageDataCredits;
  const shouldDisplayCredits = shouldShowProperty({
    isDisplayed: areImageCreditsDisplayed,
    value: creditsValue
  });
  const {
    shouldDisplayCount,
    countMessage
  } = getDisplayCountData(displayCount, images);
  const sizeKey = [IMAGE, isHero ? HERO : '', _modifier].filter(Boolean).join(':');
  const shouldDisplayImage = !!imageUrl && (priority || isIntersecting);
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    ref: outerRef,
    className: wrapperClassName,
    modifiers: modifiers
  }, otherProps), elementTitle && /*#__PURE__*/React.createElement("div", {
    className: "heading heading--section"
  }, elementTitle), shouldDisplayImage && /*#__PURE__*/React.createElement(ImgWrapper, imgWrapperProps, /*#__PURE__*/React.createElement(ImageRender, _extends({}, otherProps, {
    alt: altText,
    imageUrl: imageUrl,
    handleEnableLightbox: handleEnableLightbox,
    toggleLightbox: toggleLightbox,
    displayLightbox: displayLightbox,
    caption: caption,
    enableLightbox: enableLightbox,
    lightboxImgProps: lightboxImgProps,
    name: name,
    displayCount: displayCount,
    lightboxImages: lightboxImages,
    isHero: isHero,
    priority: priority,
    sizeKey: sizeKey
  })), children && /*#__PURE__*/React.createElement("div", {
    className: "image__children"
  }, children)), shouldDisplayCount && /*#__PURE__*/React.createElement("div", {
    className: "image__button image__button--count",
    role: "button",
    "aria-label": "Show more images",
    onClick: handleEnableLightbox
  }, /*#__PURE__*/React.createElement("p", null, countMessage), /*#__PURE__*/React.createElement("div", {
    className: "arrow arrow--right"
  })), (shouldDisplayCaption || shouldDisplayCredits) && /*#__PURE__*/React.createElement(ImageDetails, {
    caption: caption,
    credits: creditsValue,
    shouldDisplayCaption: shouldDisplayCaption,
    shouldDisplayCredits: shouldDisplayCredits
  }));
};
Image.propTypes = {
  url: PropTypes.string,
  name: PropTypes.string,
  modifier: PropTypes.string,
  imageId: PropTypes.string,
  enableLightbox: PropTypes.bool,
  toggleModal: PropTypes.func,
  handleSelectedImage: PropTypes.func,
  displayCount: PropTypes.bool,
  lightboxImages: PropTypes.array,
  images: PropTypes.array,
  isHero: PropTypes.bool,
  priority: PropTypes.bool,
  type: PropTypes.string,
  areImageCreditsDisplayed: PropTypes.bool,
  isCaptionDisplayed: PropTypes.bool,
  imageData: PropTypes.object,
  imageUrl: PropTypes.string,
  credits: PropTypes.string,
  imageCredits: PropTypes.string,
  elementTitle: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};
Image.defaultProps = {
  modifier: '',
  url: null,
  name: '',
  credits: '',
  imageCredits: '',
  enableLightbox: false,
  imageId: '',
  handleSelectedImage: () => {},
  toggleModal: () => {},
  displayCount: false,
  lightboxImages: [],
  images: [],
  isHero: false,
  priority: false,
  type: '',
  areImageCreditsDisplayed: false,
  isCaptionDisplayed: true,
  imageData: {},
  imageUrl: '',
  elementTitle: '',
  children: null
};
export default Image;
//# sourceMappingURL=Image.js.map